/**
 * Outbound-OAuth HTTP service (PX-W6.5, AC-4) — start / reconnect / callback,
 * on the shared client.
 *
 * ⚠ Today EVERY route here refuses with 400
 * `outbound-credential.provider.unsupported` (PX registers no outbound OAuth
 * provider — W2.4 ruling R5). The client wires the flow end-to-end and
 * renders the typed refusal; it never hides it as a generic failure.
 *
 * ⚠ The callback is an AUTHENTICATED XHR (never `@Public`; the provider
 * redirects to the SPA route, which then calls this endpoint with the Bearer
 * the SPA already holds — a cross-site top-level navigation carries no
 * `Authorization` header).
 */

import { apiClient } from '@/services'

import type { OAuthCallbackResponse, OAuthStartInput, OAuthStartResponse } from '../types'

export const outboundOAuthService = {
  /** Begin a connect flow → `{authorizationUrl}`. */
  start(provider: string, body: OAuthStartInput): Promise<OAuthStartResponse> {
    return apiClient.post<OAuthStartResponse>(
      `/{orgId}/outbound-oauth/${encodeURIComponent(provider)}/start`,
      body
    )
  },

  /**
   * Reconnect a dead grant — identical mechanics, reconnect intent. An
   * org-wide repair: NEVER offered per project row (ruling R1).
   */
  reconnect(provider: string, body: OAuthStartInput): Promise<OAuthStartResponse> {
    return apiClient.post<OAuthStartResponse>(
      `/{orgId}/outbound-oauth/${encodeURIComponent(provider)}/reconnect`,
      body
    )
  },

  /**
   * Complete the flow: validate + atomically spend the state, exchange,
   * seal. Called by the SPA callback page with the `code`/`state` the
   * provider appended to its redirect.
   */
  callback(code: string, state: string): Promise<OAuthCallbackResponse> {
    return apiClient.get<OAuthCallbackResponse>('/{orgId}/outbound-oauth/callback', {
      params: { code, state }
    })
  }
}
